/**
 * The Record's authoring rules (D241; `content-schema` `record.ts`).
 *
 * @remarks
 * The schema refuses a `record` that lists a flag or an ending twice, or
 * writes from no ending at all. What is left needs the pack, and each rule
 * is the reference walk or the D31/D47 dead-content law read for the Record:
 *
 * - **The declaration is real.** Every `record.remembers` flag and every
 *   `record.writtenBy` ending is declared.
 * - **`record_holds` can hold.** It needs a `record` to read at all; its
 *   `endingId` must be one the Record is written by (an ending that never
 *   writes a layer is never found in one); its `kind` must be the kind of
 *   at least one `writtenBy` ending (a `death` asked of a Record no death
 *   writes is constant-false) and, beside an `endingId`, that ending's kind.
 * - **`where` asks only what a layer knows.** Inside a layer there is no
 *   world: only `flag_equals` over `record.remembers`, joined by `all_of`,
 *   `any_of`, `at_least_of` and `not`. Any other leaf is refused, by name.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, ContentPackDefinition, RecordHoldsCondition, RecordLayerKind } from '@orpheus/content-schema';

import { brokenReference, requireDeclared } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/** Each kind as a refusal says it. */
const KIND_PHRASES: Readonly<Record<RecordLayerKind, string>> = { death: 'a death', ending: 'an ending' };

/** What a `record_holds` is judged against: the declared Record, read once. */
export interface RecordUniverse {
	/** Each `writtenBy` ending's kind — `death` for a checkpoint return, `ending` otherwise (Calder's §13.16). */
	readonly kindByEndingId: ReadonlyMap<string, RecordLayerKind>;
	/** The flags a layer remembers — all a `where` may read. */
	readonly rememberedFlagIds: ReadonlySet<string>;
}

/**
 * The declared Record as the walks read it, or nothing when the pack keeps none.
 *
 * @param contentPack - The parsed pack
 * @returns The universe, or `undefined` for a pack with no `record`
 */
export const recordUniverseOf = (contentPack: ContentPackDefinition): RecordUniverse | undefined => {
	const { record } = contentPack;

	if (record === undefined) {
		return undefined;
	}

	const endingsById = new Map(contentPack.endings.map((ending) => [ending.endingId as string, ending]));

	return {
		kindByEndingId: new Map(
			record.writtenBy.flatMap((endingId) => {
				const ending = endingsById.get(endingId);

				// An undeclared `writtenBy` ending is `validateRecordAuthoring`'s to name; it has no kind to read.
				return ending === undefined ? [] : [[endingId, ending.restartAt === 'checkpoint' ? 'death' : 'ending'] as const];
			}),
		),
		rememberedFlagIds: new Set(record.remembers),
	};
};

/**
 * Validate the pack's `record` declaration: every id it names is declared.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per broken reference, all collected
 */
export const validateRecordAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const { record } = contentPack;

	if (record === undefined) {
		return failures;
	}

	const flagIds = new Set<string>(contentPack.flags.map((flag) => flag.flagId));
	const endingIds = new Set<string>(contentPack.endings.map((ending) => ending.endingId));

	record.remembers.forEach((flagId, flagIndex) => {
		requireDeclared(flagIds, flagId, `record.remembers[${String(flagIndex)}]`, 'the Record remembers an undeclared flag', failures);
	});
	record.writtenBy.forEach((endingId, endingIndex) => {
		requireDeclared(endingIds, endingId, `record.writtenBy[${String(endingIndex)}]`, 'the Record is written by an undeclared ending', failures);
	});

	return failures;
};

/**
 * Walk one `record_holds` (see the module remarks).
 *
 * @param condition - The condition
 * @param conditionPath - The path of the condition itself
 * @param universe - The declared Record, or `undefined` when the pack keeps none
 * @param failures - Accumulator
 */
export const checkRecordHoldsCondition = (
	condition: RecordHoldsCondition,
	conditionPath: string,
	universe: RecordUniverse | undefined,
	failures: ReferenceBrokenFailure[],
): void => {
	if (universe === undefined) {
		failures.push(
			brokenReference(conditionPath, 'record_holds', 'condition reads the Record, but the pack declares no `record` — nothing is ever remembered, so it can never hold'),
		);

		return;
	}

	const endingKind = condition.endingId === undefined ? undefined : universe.kindByEndingId.get(condition.endingId);

	if (condition.endingId !== undefined && endingKind === undefined) {
		failures.push(
			brokenReference(
				`${conditionPath}.endingId`,
				condition.endingId,
				'condition asks the Record after an ending it is not written by (`record.writtenBy`) — no layer ever names it, so it can never hold',
			),
		);
	}

	if (condition.kind !== undefined && ![...universe.kindByEndingId.values()].includes(condition.kind)) {
		failures.push(
			brokenReference(`${conditionPath}.kind`, condition.kind, `no ending the Record is written by is ${KIND_PHRASES[condition.kind]}, so no layer is ever one and this can never hold`),
		);
	} else if (condition.kind !== undefined && endingKind !== undefined && endingKind !== condition.kind) {
		failures.push(brokenReference(`${conditionPath}.kind`, condition.kind, `the ending named is ${KIND_PHRASES[endingKind]}, not ${KIND_PHRASES[condition.kind]}, so this can never hold`));
	}

	if (condition.where !== undefined) {
		checkRememberedCondition(condition.where, `${conditionPath}.where`, universe, failures);
	}
};

/**
 * Walk a `where`: only what a layer remembers, joined by the combinators.
 *
 * @param condition - The condition inside the `where`
 * @param conditionPath - Its path
 * @param universe - The declared Record
 * @param failures - Accumulator
 */
const checkRememberedCondition = (condition: ConditionDefinition, conditionPath: string, universe: RecordUniverse, failures: ReferenceBrokenFailure[]): void => {
	switch (condition.type) {
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			condition.conditions.forEach((member, memberIndex) => {
				checkRememberedCondition(member, `${conditionPath}.conditions[${String(memberIndex)}]`, universe, failures);
			});
			break;
		}
		case 'flag_equals': {
			requireDeclared(
				universe.rememberedFlagIds,
				condition.flagId,
				`${conditionPath}.flagId`,
				'the Record does not remember this flag (`record.remembers`), so no layer can answer it',
				failures,
			);
			break;
		}
		case 'not': {
			checkRememberedCondition(condition.condition, `${conditionPath}.condition`, universe, failures);
			break;
		}
		default: {
			failures.push(
				brokenReference(
					conditionPath,
					condition.type,
					'inside a Record layer there is no world to read — a `where` may ask only `flag_equals` over `record.remembers`, joined by `all_of`, `any_of`, `at_least_of` and `not`',
				),
			);
		}
	}
};
